// src/components/StockComparisonChart.js
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from "recharts";
import { COLORS } from "../utils/colors";

import { METRIC_FIELDS as METRIC_OPTIONS } from "../constants/metricFields";

const formatTooltipValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toFixed(2);
  }
  return value;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ccc",
        padding: "8px 10px",
      }}
    >
      <div style={{ marginBottom: 6 }}>{label}</div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            color: entry.color || "#333",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span>{entry.name} :</span>
          <span style={{ minWidth: 56, textAlign: "right" }}>
            {formatTooltipValue(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const StockChart = ({ chartData, symbols, selectedMetric, setSelectedMetric }) => {
  if (!symbols || symbols.length === 0) return <p>No data to display.</p>;

  // 1. Collect all unique dates
// 1) Normalize: add calendarYear to each entry from its date
const withYear = {};
symbols.forEach(symbol => {
  const data = chartData[symbol] || [];
  withYear[symbol] = data.map(e => ({
    ...e,
    calendarYear: Number(new Date(e.date).getFullYear())
  }));
});

// 2) Collect all unique years
const allYearsSet = new Set();
symbols.forEach(symbol => {
  withYear[symbol]?.forEach(e => allYearsSet.add(e.calendarYear));
});
const allYears = Array.from(allYearsSet).sort((a, b) => a - b);

// 3) One row per year, columns per symbol (aligned points)
const combinedChartData = allYears.map(year => {
  const row = { calendarYear: year };
  symbols.forEach(symbol => {
    const match = withYear[symbol]?.find(e => e.calendarYear === year);
    row[symbol] = match ? match[selectedMetric] : null;
  });
  return row;
});

  return (
    <>
      <h3>Stock Comparison Chart</h3>
      <div className="comparison-chart">
        <div className="comparison-metric-selector">
          <label htmlFor="metric-select">Metric selector:</label>
          <select
            id="metric-select"
            value={selectedMetric}
            onChange={e => setSelectedMetric(e.target.value)}
          >
            {METRIC_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="comparison-chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={combinedChartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="calendarYear" 
                    type="number" 
                    domain={['dataMin','dataMax']} 
                    tick={{ fontSize: 10 }} />

              <YAxis tick={{ fontSize: 10 }} width={30} tickMargin={3} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {symbols.map((symbol, index) => (
                <Line
                  key={symbol}
                  type="monotone"
                  dataKey={symbol}
                  stroke={COLORS[index % COLORS.length]}
                  activeDot={{ r: 4 }}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

export default StockChart;
